import { Spin, message } from 'antd';
import { useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useGetHotelByParentId } from 'app/hooks/data/migrationTool/useGetHotelByParentId';
import type { IMergePayload } from 'app/hooks/data/migrationTool/useMergeHotel';
import { useMergeHotel } from 'app/hooks/data/migrationTool/useMergeHotel';

import './HotelMigration.scss';
import { Header } from './_components/Header/Header';
import { SelectActionEnum } from './_components/HotelServiceCard/HotelServiceCard';
import type { MergeActionChildService } from './_components/MergeAction/MergeAction';
import { MergeConfirmModal } from './_components/MergeConfirmModal/MergeConfirmModal';
import { MergeLoadingModal } from './_components/MergeLoadingModal/MergeLoadingModal';
import { SideMenuHotelMigration } from './_components/SideMenuHotelMigration/SideMenuHotelMigration';
import { Experiences } from './_content/Experiences/Experiences';
import { Hotel } from './_content/Hotel/Hotel';
import { Packages } from './_content/Packages/Packages';
import { Reviews } from './_content/Reviews/Reviews';
import { Rooms } from './_content/Rooms/Rooms';

export type MergeSelection = {
  action: SelectActionEnum;
  targetId?: number;
};

const TIMEOUT_ERROR_CODE = 504;

export const HotelMigration = () => {
  const { hotelId } = useParams<{ hotelId: string }>();
  const parsedHotelId = parseInt(hotelId, 10);
  const {
    data: hotels,
    isLoading,
    refetch,
  } = useGetHotelByParentId(parsedHotelId);

  const { data: hotel } = useGetHotel(parsedHotelId);

  const [selectedKey, setSelectedKey] = useState(0);
  const [childHotelId, setChildHotelId] = useState<number | undefined>(
    undefined
  );

  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isMergeLoadingModalOpen, setIsMergeLoadingModalOpen] = useState(false);
  const [isMergeHaveTimeout, setMergeHaveTimeout] = useState(false);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);

  const showMergeModal = () => setIsMergeModalOpen(true);
  const handleCancelMerge = () => setIsMergeModalOpen(false);

  const { mutateAsync: mergeHotel, isPending: isMerging } = useMergeHotel();

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);

    try {
      await refetch();

      setMergeSelections((prev) => {
        const newSelections = { ...prev };

        switch (selectedKey) {
          case 1:
            newSelections.rooms = {};
            break;
          case 2:
            newSelections.experiences = {};
            break;
          case 3:
            newSelections.packages = {};
            break;
          default:
            break;
        }

        return newSelections;
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleConfirmMerge = async () => {
    if (!childHotelId) {
      return;
    }

    const payload = buildMergePayload(
      childHotelId,
      mergeSelections.rooms as Record<number, MergeSelection>,
      mergeSelections.experiences as Record<number, MergeSelection>,
      mergeSelections.packages as Record<number, MergeSelection>
    );

    setIsMergeModalOpen(false);
    setIsMergeLoadingModalOpen(true);

    await mergeHotel(
      {
        hotelId,
        body: payload,
      },
      {
        onSuccess: () => {
          setIsMergeLoadingModalOpen(false);
          window.location.href = `/hotels/${hotelId}/edit?successfullyMerge=true`;
        },
        onError: (err: unknown) => {
          if (
            (err as Error)?.cause &&
            ((err as Error).cause as { code?: number })?.code ===
              TIMEOUT_ERROR_CODE
          ) {
            setMergeHaveTimeout(true);
          } else {
            message.error('Error merging hotels');
          }
        },
      }
    );
  };

  const buildMergePayload = (
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    hotelId: number,
    roomSelections: Record<number, MergeSelection>,
    experienceSelections: Record<number, MergeSelection>,
    packageSelections: Record<number, MergeSelection>
  ): IMergePayload => ({
    hotelId,
    rooms: Object.entries(roomSelections).map(([roomId, selection]) => ({
      roomId: Number(roomId),
      action: selection.action,
      ...(selection.targetId ? { targetId: selection.targetId } : {}),
    })),
    experiences: Object.entries(experienceSelections).map(
      ([experienceId, selection]) => ({
        experienceId: Number(experienceId),
        action: selection.action,
        ...(selection.targetId ? { targetId: selection.targetId } : {}),
      })
    ),
    packages: Object.entries(packageSelections).map(
      ([packageId, selection]) => ({
        packageId: Number(packageId),
        action: selection.action,
      })
    ),
  });

  const [mergeSelections, setMergeSelections] = useState<{
    rooms: Record<number, MergeSelection>;
    experiences: Record<number, MergeSelection>;
    packages: Record<number, MergeSelection>;
  }>({ rooms: {}, experiences: {}, packages: {} });

  const items = [
    {
      key: 0,
      label: 'Hotel',
    },
    {
      key: 1,
      label: 'Rooms',
    },
    {
      key: 2,
      label: 'Experiences',
    },
    {
      key: 3,
      label: 'Packages',
    },
    {
      key: 4,
      label: 'Reviews',
    },
  ];

  if (!hotels && !isLoading) {
    return <Redirect to={`/admin`} />;
  }

  const renderContent = () => {
    switch (selectedKey) {
      case 0:
        return (
          <Hotel
            handleSelectChild={(childId) => setChildHotelId(childId)}
            selectChildId={childHotelId}
            hotelId={parsedHotelId}
            setIsFullyLoaded={setIsFullyLoaded}
          />
        );
      case 1:
        return (
          <Rooms
            hotels={hotels}
            parentHotelId={parsedHotelId}
            childHotelId={childHotelId}
            selections={mergeSelections.rooms}
            setSelections={(newSelections) =>
              setMergeSelections((prev) => ({ ...prev, rooms: newSelections }))
            }
          />
        );
      case 2:
        return (
          <Experiences
            hotels={hotels}
            parentHotelId={parsedHotelId}
            childHotelId={childHotelId}
            selections={mergeSelections.experiences}
            setSelections={(newSelections) =>
              setMergeSelections((prev) => ({
                ...prev,
                experiences: newSelections,
              }))
            }
          />
        );
      case 3:
        return (
          <Packages
            hotels={hotels}
            parentHotelId={parsedHotelId}
            childHotelId={childHotelId}
            selections={mergeSelections.packages}
            setSelections={(newSelections) =>
              setMergeSelections((prev) => ({
                ...prev,
                packages: newSelections,
              }))
            }
          />
        );
      case 4:
        return (
          <>
            <Reviews
              hotels={hotels}
              parentHotelId={parsedHotelId}
              childHotel={hotels?.find((h) => h.hotel.id === childHotelId)}
              selections={mergeSelections}
            />
            <MergeConfirmModal
              isOpen={isMergeModalOpen}
              onCancel={handleCancelMerge}
              onConfirm={handleConfirmMerge}
              isLoading={isMerging}
            />
            <MergeLoadingModal
              isOpen={isMergeLoadingModalOpen}
              hotelId={hotelId}
              mergeHaveTimeout={isMergeHaveTimeout}
              hasCreateNewRoom={Object.values(mergeSelections.rooms).some(
                (selection) => selection.action === SelectActionEnum.CREATE_NEW
              )}
            />
          </>
        );
      default:
        return (
          <Hotel
            handleSelectChild={(childId) => setChildHotelId(childId)}
            selectChildId={childHotelId}
            hotelId={parsedHotelId}
            setIsFullyLoaded={setIsFullyLoaded}
          />
        );
    }
  };

  const firstKey = selectedKey === 0;
  const lastKey = selectedKey === items.length - 1;

  const allItemsHaveSelections = (
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    items: MergeActionChildService[],
    selections: Record<number, MergeSelection>
  ) => {
    return items.every((item) => selections[item.id]?.action !== undefined);
  };

  const formIsValid = () => {
    if (!childHotelId) {
      return false;
    }

    const parentHotel = hotels?.find((h) => h.hotel.id === parsedHotelId);
    const childHotel = hotels?.find((h) => h.hotel.id === childHotelId);

    if (!parentHotel || !childHotel) {
      return false;
    }

    switch (selectedKey) {
      case 1:
        return allItemsHaveSelections(childHotel.rooms, mergeSelections.rooms);
      case 2:
        return allItemsHaveSelections(
          childHotel.experiences,
          mergeSelections.experiences
        );
      case 3:
        return allItemsHaveSelections(
          childHotel.packages,
          mergeSelections.packages
        );
      default:
        return true;
    }
  };

  return (
    <CurrencyWrapper hotel={hotel}>
      <div className="hotel-migration">
        <SideMenuHotelMigration
          setSelectedKey={setSelectedKey}
          selectedKey={selectedKey}
          items={items}
          disabled={!formIsValid()}
        />
        <Spin
          spinning={
            !hotelId || !hotels || isLoading || !isFullyLoaded || isRefreshing
          }
          wrapperClassName="hotel-migration__spinner"
        >
          <div className="hotel-migration__content">
            <Header
              title={items[selectedKey].label}
              handleBack={() => setSelectedKey(selectedKey - 1)}
              handleNext={() => setSelectedKey(selectedKey + 1)}
              handleRefresh={handleRefresh}
              isBackHidden={firstKey}
              isNextDisabled={lastKey || !formIsValid()}
              isLastStep={lastKey}
              onMergeClick={showMergeModal}
            />
            <MergeConfirmModal
              isOpen={isMergeModalOpen}
              onCancel={handleCancelMerge}
              onConfirm={handleConfirmMerge}
              isLoading={isMerging}
            />
            {renderContent()}
          </div>
        </Spin>
      </div>
    </CurrencyWrapper>
  );
};
